import Immutable from 'immutable';
import { AppState, AppStateActionParams } from 'records/AppState';
import { NewPunch } from 'records/NewPunch';
import { Punch, PunchMap } from 'records/Punch';
import {
  createPunch,
  deletePunch,
  getPunches,
  updatePunch as updatePunchService,
} from 'services/punchService';

export interface PunchActions {
  getPunches: (
    actionParams: AppStateActionParams,
    projectId: string
  ) => Promise<AppState>;

  createPunch: (
    actionParams: AppStateActionParams,
    punch: NewPunch
  ) => Promise<AppState>;

  updatePunch: (
    actionParams: AppStateActionParams,
    punch: Punch,
    uploadedPhotos: File[],
    insertedPhotoIds: string[]
  ) => IterableIterator<AppState | Promise<AppState>>;

  updatePunches: (
    actionParams: AppStateActionParams,
    punches: Punch[]
  ) => Promise<AppState>;

  deletePunch: (
    actionParams: AppStateActionParams,
    punch: Punch
  ) => Promise<AppState>;
}

const punchActions: PunchActions = {
  getPunches: async (
    { state }: AppStateActionParams,
    projectId: string
  ): Promise<AppState> => {
    const punches = await getPunches(projectId);

    const punchesByKey = punches.reduce(
      (acc: PunchMap, punch: Punch): PunchMap => acc.set(punch.id, punch),
      Immutable.Map<string, Punch>()
    );

    return state().set('punches', punchesByKey);
  },

  createPunch: async (
    { state, actions }: AppStateActionParams,
    punch: NewPunch
  ): Promise<AppState> => {
    const createdPunch = await createPunch(punch);

    // TODO: right now this errors out, as I believe the actual markup is not yet really
    //       created on the server. In the future this would be a good thing to do.
    // fetch any markups that belong to the created punch.
    // try {
    //   await Promise.all(
    //     createdPunch.markupids.map((m) =>
    //       actions.getMarkup(m, createdPunch.projectid)
    //     )
    //   );
    // } catch (err) {
    //   console.log('createPunch: ', err);
    // }

    await actions.queryPunches();

    return state().setIn(['punches', createdPunch.id], createdPunch);
  },

  updatePunch: function* updatePunch(
    { state, actions }: AppStateActionParams,
    punch: Punch,
    uploadedPhotos: File[] = [],
    insertedPhotoIds: string[] = []
  ): IterableIterator<AppState | Promise<AppState>> {
    const allCurrentPunchAssigness = state()
      .punches.valueSeq()
      .reduce(
        (acc, p) => acc.concat(p.assignees),
        Immutable.OrderedSet<string>()
      );
    const allCurrentPunchGroupAssigness = state()
      .punches.valueSeq()
      .reduce(
        (acc, p) => acc.concat(p.groupAssignees),
        Immutable.OrderedSet<string>()
      );

    // optimistic updating of punch.
    yield state().setIn(['punches', punch.id], punch);

    yield (async (): Promise<AppState> => {
      const updatedPunch = await updatePunchService(
        punch,
        uploadedPhotos,
        insertedPhotoIds
      );
      const { punchQuery } = state();

      if (punchQuery.assignees.size > 0 || punchQuery.groupAssignees.size) {
        // assignees in the allCurrentPunchAssigness, but NOT in punchQuery (not selected)
        const filteredAssignees = allCurrentPunchAssigness.subtract(
          punchQuery.assignees
        );

        const extraAssignees =
          updatedPunch.assignees.subtract(filteredAssignees);
        await actions.addAssigneesToPunchQuery(extraAssignees);
        const filteredGroupAssignees = allCurrentPunchGroupAssigness.subtract(
          punchQuery.groupAssignees
        );

        const extraGroupAssignees = updatedPunch.groupAssignees.subtract(
          filteredGroupAssignees
        );

        await actions.addGroupAssigneesToPunchQuery(extraGroupAssignees);
      }

      await actions.queryPunches();

      return state().setIn(['punches', updatedPunch.id], updatedPunch);
    })();
  },

  updatePunches: async (
    { state }: AppStateActionParams,
    punches: Punch[]
  ): Promise<AppState> => {
    try {
      const updatedPunches = await Promise.all(
        punches.map((p) => updatePunchService(p))
      );

      const updatedPunchesByKey = updatedPunches.reduce(
        (acc: PunchMap, punch: Punch): PunchMap => acc.set(punch.id, punch),
        Immutable.Map<string, Punch>()
      );

      return state().mergeIn(['punches'], updatedPunchesByKey);
    } catch (err) {
      return state();
    }
  },

  deletePunch: async (
    { state }: AppStateActionParams,
    punch: Punch
  ): Promise<AppState> => {
    const deletedPunchId = await deletePunch(punch);

    return state()
      .update('punches', (punches) => punches.delete(deletedPunchId))
      .update('markups', (markups) =>
        markups.map((mm) => {
          return punch.markupids.reduce((acc, mid) => {
            return acc.delete(mid);
          }, mm);
        })
      );
  },
};
export default punchActions;
